import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { DonorAvatar } from '@/features/donorAvatar/DonorAvatar'
import { DonorMascot } from '@/features/donorMascot/DonorMascot'
import { ACHIEVEMENT_DEFINITIONS } from '@/lib/landing-achievement-definitions'
import {
  formatTopDonorAmount,
  topDonorInitial,
  type LandingTopDonor,
} from '@/lib/landing-top-donors'
import styles from './TopDonorProfileDialog.module.css'

type Props = {
  donor: LandingTopDonor
  onClose: () => void
}

export function TopDonorProfileDialog({ donor, onClose }: Props) {
  const dialog = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const element = dialog.current
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    element?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      element?.close()
      document.body.style.overflow = previousOverflow
      previousFocus?.focus({ preventScroll: true })
    }
  }, [])

  const badges = ACHIEVEMENT_DEFINITIONS.filter((badge) =>
    donor.achievement_ids?.includes(badge.id)
  )
  const count = donor.paid_purchase_count

  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="top-donor-profile-title"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label="Fechar perfil"
      >
        <Icon icon="mdi:close" aria-hidden />
      </button>
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>
            <Icon icon="mdi:trophy-award" aria-hidden /> PERFIL DA CAMPANHA
          </span>
          <span className={styles.rank}>{donor.rank}º lugar no ranking</span>
          <h2 id="top-donor-profile-title">{donor.display_name}</h2>
          <p>
            Um pouco da jornada de quem apoia esta campanha. O nome permanece protegido.
          </p>
        </header>

        <div className={styles.characters}>
          <div className={styles.characterCard}>
            <span className={styles.characterLabel}>AVATAR</span>
            <div className={styles.avatarFrame}>
              {donor.avatar ? (
                <DonorAvatar
                  config={donor.avatar}
                  label={`Avatar de ${donor.display_name}`}
                />
              ) : donor.profile_photo_url ? (
                <img
                  src={donor.profile_photo_url}
                  alt={`Foto de ${donor.display_name}`}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span
                  className={styles.avatarFallback}
                  aria-label={`Avatar de ${donor.display_name}`}
                >
                  {topDonorInitial(donor.display_name)}
                </span>
              )}
            </div>
          </div>
          <div className={`${styles.characterCard} ${styles.mascotCard}`}>
            <span className={styles.characterLabel}>MASCOTE</span>
            <div className={styles.mascotFrame}>
              {donor.mascot ? (
                <DonorMascot config={donor.mascot} showBackdrop={false} />
              ) : (
                <Icon
                  icon="mdi:paw-outline"
                  className={styles.mascotFallback}
                  aria-hidden
                />
              )}
            </div>
            {donor.mascot && donor.mascot_level != null ? (
              <span className={styles.mascotLevel} aria-label={`Mascote no nível ${donor.mascot_level}`}>
                <Icon icon="mdi:star-four-points" aria-hidden />
                NÍVEL <b>{donor.mascot_level}</b>
              </span>
            ) : null}
            <strong>
              {donor.mascot?.name ||
                (donor.mascot ? 'Companheiro de jornada' : 'Mascote ainda não criado')}
            </strong>
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <Icon icon="mdi:heart-circle" aria-hidden />
            <div>
              <span>Total em compras nesta campanha</span>
              <strong>{formatTopDonorAmount(donor.total_amount)}</strong>
            </div>
          </div>
          <div className={styles.stat}>
            <Icon icon="mdi:hand-heart-outline" aria-hidden />
            <div>
              <span>Compras confirmadas</span>
              <strong>{count == null ? '—' : count}</strong>
            </div>
          </div>
        </div>

        <section className={styles.achievements} aria-labelledby="top-donor-badges-title">
          <div className={styles.sectionHeading}>
            <div>
              <span className={styles.eyebrow}>JORNADA DE APOIO</span>
              <h3 id="top-donor-badges-title">Selos conquistados</h3>
            </div>
            <span className={styles.badgeCount}>
              {donor.achievement_ids == null ? '—' : badges.length}
            </span>
          </div>
          {donor.achievement_ids == null ? (
            <p className={styles.emptyBadges}>
              Os selos deste comprador estão indisponíveis no momento.
            </p>
          ) : badges.length === 0 ? (
            <p className={styles.emptyBadges}>
              Este comprador ainda não conquistou selos nesta campanha.
            </p>
          ) : (
            <ul className={styles.badges}>
              {badges.map((badge) => (
                <li key={badge.id} className={styles.badge}>
                  <img src={badge.image} alt="" />
                  <span>{badge.title}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </dialog>,
    document.body
  )
}
